import {
  Logout as LogoutIcon,
  Computer as ComputerIcon,
  DeviceUnknown as DeviceUnknownIcon,
  LocationOn as LocationIcon,
  PhoneAndroid as PhoneAndroidIcon,
  PhoneIphone as PhoneIphoneIcon,
  Schedule as ScheduleIcon,
  TabletAndroid as TabletIcon,
  TabletMac as TabletMacIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  _IconButton,
  Stack,
  Typography,
  Collapse,
  Divider,
} from '@mui/material';
import moment from 'moment';
import 'moment/locale/es'; // Import Spanish locale
import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  getFriendlyBrowserName,
  getFriendlyDeviceType,
  getFriendlyIPAddress,
  getFriendlyLocation,
  getFriendlyOSName,
} from 'shared/deviceUtils';
import { fetchUserSessions, revokeAllOtherSessions, revokeSession } from '../../SessionsActions';
import {
  getRevokingAllStatus,
  getRevokingStatus,
  getSessions,
  getSessionsError,
  getSessionsLoading,
} from '../../SessionsReducer';
import { getTwoFactorStatus } from '../../../Auth/TwoFactorReducer';
import { get2FAStatus } from '../../../Auth/TwoFactorActions';
import { Link } from 'react-router-dom';

const messages = defineMessages({
  title: {
    id: 'Sessions.Title',
    defaultMessage: 'Active Sessions',
  },
  description: {
    id: 'Sessions.Description',
    defaultMessage:
      'These are the devices that are currently signed in to your account. You can revoke access for any device.',
  },
  currentSession: {
    id: 'Sessions.CurrentSession',
    defaultMessage: 'Current Session',
  },
  revokeSession: {
    id: 'Sessions.RevokeSession',
    defaultMessage: 'Sign Out',
  },
  revokeAllOthers: {
    id: 'Sessions.RevokeAllOthers',
    defaultMessage: 'Sign Out All Other Devices',
  },
  confirmRevokeTitle: {
    id: 'Sessions.ConfirmRevokeTitle',
    defaultMessage: 'Sign Out Device',
  },
  confirmRevokeMessage: {
    id: 'Sessions.ConfirmRevokeMessage',
    defaultMessage:
      'Are you sure you want to sign out this device? You will need to sign in again on that device.',
  },
  confirmRevokeAllTitle: {
    id: 'Sessions.ConfirmRevokeAllTitle',
    defaultMessage: 'Sign Out All Other Devices',
  },
  confirmRevokeAllMessage: {
    id: 'Sessions.ConfirmRevokeAllMessage',
    defaultMessage:
      'Are you sure you want to sign out all other devices? You will need to sign in again on those devices.',
  },
  confirm: {
    id: 'Sessions.Confirm',
    defaultMessage: 'Confirm',
  },
  cancel: {
    id: 'Sessions.Cancel',
    defaultMessage: 'Cancel',
  },
  lastUsed: {
    id: 'Sessions.LastUsed',
    defaultMessage: 'Last used {time}',
  },
  created: {
    id: 'Sessions.Created',
    defaultMessage: 'Created {time}',
  },
  unknownLocation: {
    id: 'Sessions.UnknownLocation',
    defaultMessage: 'Unknown location',
  },
  unknownDevice: {
    id: 'Sessions.UnknownDevice',
    defaultMessage: 'Unknown device',
  },
  unknownBrowser: {
    id: 'Sessions.UnknownBrowser',
    defaultMessage: 'Unknown browser',
  },
  unknownOS: {
    id: 'Sessions.UnknownOS',
    defaultMessage: 'Unknown OS',
  },
  browserOnOS: {
    id: 'Sessions.BrowserOnOS',
    defaultMessage: '{browser} on {os}',
  },
  noOtherSessions: {
    id: 'Sessions.NoOtherSessions',
    defaultMessage: 'No other active sessions',
  },
  errorLoading: {
    id: 'Sessions.ErrorLoading',
    defaultMessage: 'Error loading sessions. Please try again.',
  },
  securityWarning: {
    id: 'Sessions.SecurityWarning',
    defaultMessage: 'Security Warning',
  },

  riskLevel: {
    id: 'Sessions.RiskLevel',
    defaultMessage: 'Risk Level: {level}',
  },
  securityIssues: {
    id: 'Sessions.SecurityIssues',
    defaultMessage: '{count} security issues detected',
  },
  riskLevelLow: {
    id: 'Sessions.RiskLevel.Low',
    defaultMessage: 'Low',
  },
  riskLevelMedium: {
    id: 'Sessions.RiskLevel.Medium',
    defaultMessage: 'Medium',
  },
  riskLevelHigh: {
    id: 'Sessions.RiskLevel.High',
    defaultMessage: 'High',
  },
  securityOverview: {
    id: 'Sessions.SecurityOverview',
    defaultMessage: 'Security Overview',
  },
  totalSessions: {
    id: 'Sessions.TotalSessions',
    defaultMessage: 'Total Sessions: {count}',
  },
  highRiskSessions: {
    id: 'Sessions.HighRiskSessions',
    defaultMessage: 'High Risk: {count}',
  },
  vpnSessions: {
    id: 'Sessions.VpnSessions',
    defaultMessage: 'VPN/Proxy: {count}',
  },
  suspiciousActivity: {
    id: 'Sessions.SuspiciousActivity',
    defaultMessage: 'Suspicious Activity Detected',
  },
  securityRecommendations: {
    id: 'Sessions.SecurityRecommendations',
    defaultMessage: 'Security Recommendations',
  },
  reviewHighRisk: {
    id: 'Sessions.ReviewHighRisk',
    defaultMessage: 'Review high-risk sessions immediately',
  },
  enable2FA: {
    id: 'Sessions.Enable2FA',
    defaultMessage: 'Enable two-factor authentication for additional security',
  },
  contactSupport: {
    id: 'Sessions.ContactSupport',
    defaultMessage: 'Contact support if you notice suspicious activity',
  },
  showSecurityDetails: {
    id: 'Sessions.ShowSecurityDetails',
    defaultMessage: 'Show security details',
  },
  hideSecurityDetails: {
    id: 'Sessions.HideSecurityDetails',
    defaultMessage: 'Hide security details',
  },

  allSecure: {
    id: 'Sessions.AllSecure',
    defaultMessage:
      'All your active sessions appear to be secure. Continue to monitor for any unusual activity.',
  },
  securityIssuesCount: {
    id: 'Sessions.SecurityIssuesCount',
    defaultMessage: '{count} security issue{count, plural, one {} other {s}} detected',
  },
  noSecurityIssues: {
    id: 'Sessions.NoSecurityIssues',
    defaultMessage: 'No security issues detected',
  },
});

const getDeviceIcon = ({ deviceType, userAgent, os }) => {
  const friendlyType = getFriendlyDeviceType(deviceType, userAgent);

  if (friendlyType) {
    const typeLower = friendlyType.toLowerCase();
    if (typeLower.includes('mobile') || typeLower.includes('phone')) {
      return os === 'ios' ? <PhoneIphoneIcon /> : <PhoneAndroidIcon />;
    }
    if (typeLower.includes('tablet')) {
      return os === 'ios' ? <TabletMacIcon /> : <TabletIcon />;
    }
    if (
      typeLower.includes('desktop') ||
      typeLower.includes('laptop') ||
      typeLower.includes('computer')
    ) {
      return <ComputerIcon />;
    }
  }

  return <DeviceUnknownIcon />;
};

// Use the utility function from deviceUtils for consistent location formatting

function SessionItem({ session, isCurrentSession, onRevoke }) {
  const { formatMessage } = useIntl();
  const isRevoking = useSelector(getRevokingStatus(session._id));
  const [showSecurityDetails, setShowSecurityDetails] = useState(false);

  const deviceInfo = session.deviceInfo || {};

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="flex-start" gap={2} flex={1}>
            <Box sx={{ mt: 0.5 }}>{getDeviceIcon(deviceInfo)}</Box>

            <Box flex={1}>
              <Stack direction="row" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle1">
                  {deviceInfo.os ? (
                    <FormattedMessage
                      {...messages.browserOnOS}
                      values={{
                        browser:
                          getFriendlyBrowserName(deviceInfo.browser, deviceInfo.userAgent) ||
                          formatMessage(messages.unknownBrowser),
                        os:
                          getFriendlyOSName(deviceInfo.os, deviceInfo.userAgent) ||
                          formatMessage(messages.unknownOS),
                      }}
                    />
                  ) : (
                    getFriendlyBrowserName(deviceInfo.browser, deviceInfo.userAgent) ||
                    formatMessage(messages.unknownBrowser)
                  )}
                </Typography>

                {isCurrentSession && (
                  <Chip
                    label={formatMessage(messages.currentSession)}
                    color="primary"
                    size="small"
                  />
                )}
                {['HIGH', 'MEDIUM'].includes(session.securityAnalysis?.riskLevel) && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      session.securityAnalysis?.suspiciousFlags?.length > 0
                        ? setShowSecurityDetails(!showSecurityDetails)
                        : null
                    }
                    startIcon={
                      session.securityAnalysis.riskLevel === 'HIGH' ? (
                        <SecurityIcon />
                      ) : session.securityAnalysis.riskLevel === 'MEDIUM' ? (
                        <WarningIcon />
                      ) : null
                    }
                    endIcon={
                      session.securityAnalysis?.suspiciousFlags?.length > 0 ? (
                        showSecurityDetails ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )
                      ) : null
                    }
                    color={
                      session.securityAnalysis.riskLevel === 'HIGH'
                        ? 'error'
                        : session.securityAnalysis.riskLevel === 'MEDIUM'
                          ? 'warning'
                          : 'success'
                    }
                    sx={{
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      textTransform: 'none',
                      cursor: session.securityAnalysis?.suspiciousFlags?.length
                        ? 'pointer'
                        : 'default',
                      '&:hover': {
                        backgroundColor: session.securityAnalysis?.suspiciousFlags?.length
                          ? 'action.hover'
                          : 'transparent',
                      },
                      py: 0,
                    }}
                    disableRipple={!session.securityAnalysis?.suspiciousFlags?.length}
                  >
                    {formatMessage(messages.riskLevel, {
                      level: formatMessage(
                        session.securityAnalysis.riskLevel === 'HIGH'
                          ? messages.riskLevelHigh
                          : messages.riskLevelMedium,
                      ),
                    })}
                  </Button>
                )}
              </Stack>

              <Stack spacing={0.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {getFriendlyLocation(session.location) ||
                      formatMessage(messages.unknownLocation)}
                    {deviceInfo.ip && ` • ${getFriendlyIPAddress(deviceInfo.ip)}`}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      {...messages.lastUsed}
                      values={{
                        time: moment(session.lastUsed).fromNow(),
                      }}
                    />
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  <FormattedMessage
                    {...messages.created}
                    values={{
                      time: moment(session.createdAt).fromNow(),
                    }}
                  />
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {!isCurrentSession && (
              <Button
                onClick={() => onRevoke(session._id)}
                disabled={isRevoking}
                color="error"
                size="small"
                startIcon={<LogoutIcon />}
              >
                {formatMessage(messages.revokeSession)}
              </Button>
            )}
          </Box>
        </Box>

        {/* Simplified Security Details */}
        {['HIGH', 'MEDIUM'].includes(session.securityAnalysis?.riskLevel) && (
          <Collapse in={showSecurityDetails}>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <FormattedMessage
                  {...messages.securityIssuesCount}
                  values={{ count: session.securityAnalysis.suspiciousFlags.length }}
                />
              </Typography>

              {session.securityAnalysis.suspiciousFlags.map((flag, index) => (
                <Box key={index} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.primary">
                    {flag.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
}

function SessionsPage() {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const sessions = useSelector(getSessions);
  const loading = useSelector(getSessionsLoading);
  const error = useSelector(getSessionsError);
  const revokingAll = useSelector(getRevokingAllStatus);
  const twoFactorStatus = useSelector(getTwoFactorStatus);
  const passkeys = useSelector(state => state.passkey?.passkeys || []);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, sessionId: null });

  // Get current session (the one with the most recent lastUsed that's active)
  const sessionsArray = Array.isArray(sessions) ? sessions : [];

  const currentSession = sessionsArray
    .filter(s => s.isActive)
    .sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed))[0];

  const otherSessions = sessionsArray.filter(s => s.isActive && s._id !== currentSession?._id);

  useEffect(() => {
    dispatch(fetchUserSessions());
    dispatch(get2FAStatus());
  }, [dispatch]);

  const handleRevokeSession = sessionId => {
    setConfirmDialog({ open: true, type: 'single', sessionId });
  };

  const handleRevokeAllOthers = () => {
    setConfirmDialog({ open: true, type: 'all', sessionId: null });
  };

  const handleConfirm = async () => {
    try {
      if (confirmDialog.type === 'single') {
        await dispatch(revokeSession(confirmDialog.sessionId));
      } else {
        await dispatch(revokeAllOtherSessions());
      }
    } catch (err) {
      console.error('Error revoking session:', err);
    }
    setConfirmDialog({ open: false, type: null, sessionId: null });
  };

  const handleCancel = () => {
    setConfirmDialog({ open: false, type: null, sessionId: null });
  };

  if (loading && sessions.length === 0) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {formatMessage(messages.title)}
        </Typography>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        {formatMessage(messages.title)}
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        {formatMessage(messages.description)}
      </Typography>

      {/* Security Overview Section */}
      {sessionsArray.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {formatMessage(messages.securityOverview)}
            </Typography>

            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {formatMessage(messages.totalSessions, {
                    count: sessionsArray.filter(s => s.isActive).length,
                  })}
                </Typography>
              </Box>

              {(() => {
                const highRiskSessions = sessionsArray.filter(
                  s => s.isActive && s.securityAnalysis?.riskLevel === 'HIGH',
                ).length;
                if (highRiskSessions > 0) {
                  return (
                    <Box>
                      <Typography variant="body2" color="error">
                        {formatMessage(messages.highRiskSessions, { count: highRiskSessions })}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}

              {(() => {
                const vpnSessions = sessionsArray.filter(
                  s =>
                    s.isActive &&
                    s.deviceInfo?.maxMindTraits &&
                    (s.deviceInfo.maxMindTraits.isAnonymousProxy ||
                      s.deviceInfo.maxMindTraits.isAnonymousVpn ||
                      s.deviceInfo.maxMindTraits.isTorExitNode),
                ).length;
                if (vpnSessions > 0) {
                  return (
                    <Box>
                      <Typography variant="body2" color="warning.main">
                        {formatMessage(messages.vpnSessions, { count: vpnSessions })}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}
            </Stack>

            {/* Security Recommendations */}
            {(() => {
              const hasHighRisk = sessionsArray.some(
                s => s.isActive && s.securityAnalysis?.riskLevel === 'HIGH',
              );
              const hasVpn = sessionsArray.some(
                s =>
                  s.isActive &&
                  s.deviceInfo?.maxMindTraits &&
                  (s.deviceInfo.maxMindTraits.isAnonymousProxy ||
                    s.deviceInfo.maxMindTraits.isAnonymousVpn ||
                    s.deviceInfo.maxMindTraits.isTorExitNode),
              );

              if (hasHighRisk || hasVpn) {
                return (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {formatMessage(messages.suspiciousActivity)}
                    </Typography>
                    <Typography variant="body2" component="div">
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {hasHighRisk && <li>{formatMessage(messages.reviewHighRisk)}</li>}
                        {!twoFactorStatus?.enabled && passkeys.length === 0 && (
                          <li>{formatMessage(messages.enable2FA)}</li>
                        )}
                        <li>
                          <FormattedMessage
                            id="Sessions.ChangePassword"
                            defaultMessage="<link>Change your password </link> to protect your account"
                            values={{
                              link: chunks => (
                                <Box
                                  component={Link}
                                  to="/account/profile"
                                  sx={{ color: 'inherit', textDecoration: 'underline' }}
                                >
                                  {chunks}
                                </Box>
                              ),
                            }}
                          />
                        </li>
                        <li>{formatMessage(messages.contactSupport)}</li>
                      </ul>
                    </Typography>
                  </Alert>
                );
              }
              if (sessionsArray.length > 0) {
                // Check if there are any medium risk sessions
                const mediumRiskSessions = sessionsArray.filter(
                  s => s.isActive && s.securityAnalysis?.riskLevel === 'MEDIUM',
                ).length;

                if (mediumRiskSessions > 0) {
                  // Show warning for medium risk sessions
                  return (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {formatMessage(messages.securityRecommendations)}
                      </Typography>
                      <Typography variant="body2" component="div">
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                          <li>
                            <FormattedMessage
                              id="Sessions.ReviewMediumRisk"
                              defaultMessage="Review medium-risk sessions and consider <link>changing your password</link> if you see something unusual"
                              values={{
                                link: chunks => (
                                  <Box
                                    component={Link}
                                    to="/account/profile"
                                    sx={{ color: 'inherit', textDecoration: 'underline' }}
                                  >
                                    {chunks}
                                  </Box>
                                ),
                              }}
                            />
                          </li>
                          {!twoFactorStatus?.enabled && passkeys.length === 0 && (
                            <li>{formatMessage(messages.enable2FA)}</li>
                          )}
                        </ul>
                      </Typography>
                    </Alert>
                  );
                }
                // Show positive security status when no issues
                return (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {formatMessage(messages.noSecurityIssues)}
                    </Typography>
                    <Typography variant="body2">{formatMessage(messages.allSecure)}</Typography>
                  </Alert>
                );
              }
              return null;
            })()}
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formatMessage(messages.errorLoading)}
        </Alert>
      )}

      {/* Current Session */}
      {currentSession && (
        <Box mb={3}>
          <SessionItem session={currentSession} isCurrentSession onRevoke={handleRevokeSession} />
        </Box>
      )}

      {/* Other Sessions */}
      {otherSessions.length > 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1">Other Devices ({otherSessions.length})</Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleRevokeAllOthers}
              disabled={revokingAll}
              startIcon={<LogoutIcon />}
            >
              {formatMessage(messages.revokeAllOthers)}
            </Button>
          </Box>

          {otherSessions.map(session => (
            <SessionItem
              key={session._id}
              session={session}
              isCurrentSession={false}
              onRevoke={handleRevokeSession}
            />
          ))}
        </Box>
      )}

      {otherSessions.length === 0 && currentSession && (
        <Alert severity="info">{formatMessage(messages.noOtherSessions)}</Alert>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCancel}>
        <DialogTitle>
          {confirmDialog.type === 'single'
            ? formatMessage(messages.confirmRevokeTitle)
            : formatMessage(messages.confirmRevokeAllTitle)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === 'single'
              ? formatMessage(messages.confirmRevokeMessage)
              : formatMessage(messages.confirmRevokeAllMessage)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{formatMessage(messages.cancel)}</Button>
          <Button onClick={handleConfirm} color="error" variant="contained" disabled={revokingAll}>
            {formatMessage(messages.confirm)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SessionsPage;
